import useMatestoreStore from '@/store/matestoreStore';
import { useGlobalRouteStore } from '@/store/globalRouteStore';
import { useRoute } from 'vue-router';
import { $message } from '@nio-fe/lego';
import { maxBy } from 'lodash-es';

export const useMicroAppItemActions = () => {
  const microStore = useMatestoreStore();
  const routeStore = useGlobalRouteStore();
  const route = useRoute();

  const handleToTop = (microAppName: string) => {
    microStore
      .changeMicroAppSorting(microAppName, 0, 0, '', 'frequently')
      .then(() => {
        $message.success(`已置顶`);
      });
  };
  const handleToBottom = (microAppName: string) => {
    microStore
      .changeMicroAppSorting(microAppName, 0, 999, '', 'frequently')
      .then(() => {
        $message.success(`已置底`);
      });
  };

  const handleSort = (microAppName: string, type: 'up' | 'down') => {
    const sortItem = microStore.sortingMap[microAppName];
    if (!sortItem) {
      return;
    }
    const { sort, sortForFreq } = sortItem;
    const newSort = type === 'up' ? sortForFreq - 1 : sortForFreq + 1;
    microStore
      .changeMicroAppSorting(microAppName, 0, newSort, '', 'frequently')
      .then(() => {
        $message.success(`调整完成`);
      });
  };

  const handlePin = (microAppName: string) => {
    microStore
      .changeMicroAppSorting(microAppName, 0, 0, '', 'frequently')
      .then(() => {
        $message.success(`已固定至侧栏`);
      });
  };
  const handleUnpin = (microAppName: string) => {
    microStore
      .changeMicroAppSorting(microAppName, 0, 0, '', 'more')
      .then(() => {
        $message.success(`已从侧栏取消固定`);
      });
    const item = microStore.findMicroApp(microAppName);
    if (!item) {
      return;
    }
    item.activateAt = new Date().getTime();
    microStore.generateTemporaryOpenedAppList();
  };

  const handleToggleIm = () => {
    if (!microStore.showIm) {
      microStore.openImMicro();
    }
  };

  const handleClickMicroApp = (microAppName: string) => {
    if (microAppName === 'dashboard') {
      /*open first dashboard micro*/
      const firstDashboardMicroApp = microStore.dashboardMicroApps[0];
      if (firstDashboardMicroApp.fullPath) {
        routeStore.push(firstDashboardMicroApp.fullPath);
      }
      return;
    }
    if (['message', 'im'].includes(microAppName)) {
      handleToggleIm();
    } else {
      const item = microStore.findMicroApp(microAppName);
      if (!item) {
        return;
      }
      const currentApp = routeStore.getCurrentAppFromRoute();
      if (microStore.showIm) {
        microStore.closeImMicro();
        // https://sentry-tracker.nioint.com/organizations/nio-internal/issues/8146/?environment=prod&project=19&query=is%3Aunresolved&referrer=issue-stream
        if (currentApp?.name === item.name) {
          return;
        }
      }
      if (item.latestUsedPath) {
        if (item.latestUsedPath === routeStore.currentRoute?.fullPath) {
          routeStore.push(item.fullPath as string);
        } else {
          routeStore.push(item.latestUsedPath);
        }
      } else {
        routeStore.push(item.fullPath as string);
      }
      microStore.closeImMicro();
    }
  };

  const handleGoToHomepage = (microAppName: string) => {
    const item = microStore.findMicroApp(microAppName);
    if (!item) {
      return;
    }
    if (item.latestUsedPath) {
      if (item.latestUsedPath === routeStore.currentRoute?.fullPath) {
        routeStore.push(item.fullPath as string);
      } else {
        routeStore.push(item.latestUsedPath);
      }
    } else {
      routeStore.push(item.fullPath as string);
    }
  };

  const handleCloseMicro = (microAppName: string) => {
    microStore.removeMicroAppFromTemporaryZone(microAppName);
  };

  const isMicroAppActivated = (microAppName: string) => {
    if (microAppName === 'message') {
      return microStore.showIm;
    }
    if (microStore.showIm) {
      return false;
    }
    if (microAppName === 'dashboard') {
      const dashboardMicroApps = microStore.dashboardMicroApps;
      return (
        route.name === 'dashboard' ||
        !!dashboardMicroApps.find((i) => i.name === route.name)
      );
    }
    const microApp = microStore.findMicroApp(microAppName);
    return microApp?.activated;
  };

  const couldGoUp = (microAppName: string) => {
    const sortItem = microStore.sortingMap[microAppName];
    if (!sortItem) {
      return false;
    }
    return sortItem.sortForFreq > 0;
  };

  const couldGoDown = (microAppName: string) => {
    const latestOne = maxBy(microStore.sortingList, 'sortForFreq');
    if (!latestOne) {
      return false;
    }
    return latestOne.name !== microAppName;
  };

  return {
    handlePin,
    handleUnpin,
    handleToggleIm,
    handleClickMicroApp,
    handleGoToHomepage,
    handleCloseMicro,
    isMicroAppActivated,
    couldGoDown,
    couldGoUp,
    handleToBottom,
    handleToTop,
    handleSort,
  };
};
